"use client";

import { useState, useEffect, useRef } from "react";

interface ImageGalleryProps {
  images: Array<{ url: string }>;
  alt: string;
  autoplay?: boolean;
}

export default function ImageGallery({ images, alt, autoplay = false }: ImageGalleryProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [nextIndex, setNextIndex] = useState(0);
  const [showOverlay, setShowOverlay] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showMagnifier, setShowMagnifier] = useState(false);
  const [magnifierPos, setMagnifierPos] = useState({ x: 0, y: 0 });
  const imageRef = useRef<HTMLDivElement>(null);

  const handleImageChange = (newIndex: number) => {
    if (newIndex === selectedIndex) return;
    
    setNextIndex(newIndex);
    setShowOverlay(true);
    
    // Force a reflow to ensure the overlay starts at opacity-0
    requestAnimationFrame(() => {
      // After overlay is mounted, start the fade in
      requestAnimationFrame(() => {
        const overlay = document.querySelector('.image-overlay') as HTMLElement;
        if (overlay) {
          overlay.style.opacity = '1';
        }
      });
    });

    // After transition completes, update the base image and hide overlay
    setTimeout(() => {
      setSelectedIndex(newIndex);
      setShowOverlay(false);
    }, 1000);
  };

  // Autoplay functionality
  useEffect(() => {
    if (!autoplay || !images || images.length <= 1) return;

    const interval = setInterval(() => {
      const nextIndex = (selectedIndex + 1) % images.length;
      handleImageChange(nextIndex);
    }, 3000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndex, images, autoplay]);

  // Handle mouse movement for magnifier
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const rect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setMagnifierPos({ x, y });
  };

  // Handle touch movement for magnifier
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!imageRef.current) return;

    const touch = e.touches[0];
    const rect = imageRef.current.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;

    setMagnifierPos({ x, y });
    setShowMagnifier(true);
  };

  // Get high-res version of image from Cloudinary
  const getHighResUrl = (url: string) => {
    // Cloudinary URLs have transformations like /w_800,h_800/
    // We want to request a much larger size for the magnifier
    // Replace any width/height transformations with a larger size
    if (url.includes('res.cloudinary.com')) {
      // Replace width and height transformations with larger values
      return url
        .replace(/\/w_\d+/g, '/w_2000')  // Request 2000px width
        .replace(/\/h_\d+/g, '/h_2000')  // Request 2000px height
        .replace(/\/c_\w+/g, '/c_limit'); // Use limit crop to maintain aspect ratio
    }
    return url;
  };

  // Calculate magnifier position and clip
  const getMagnifierStyle = () => {
    if (!imageRef.current) return { container: {}, wrapper: {} };

    const rect = imageRef.current.getBoundingClientRect();
    const zoomLevel = 2.5;
    const magnifierSize = 300;
    const halfSize = magnifierSize / 2;

    // Calculate magnifier box position
    let boxLeft = magnifierPos.x - halfSize;
    let boxTop = magnifierPos.y - halfSize;

    // Keep magnifier box inside the container
    boxLeft = Math.max(0, Math.min(boxLeft, rect.width - magnifierSize));
    boxTop = Math.max(0, Math.min(boxTop, rect.height - magnifierSize));

    // Create a wrapper that matches the original container size
    // Scale from center, then translate to show the correct area
    const scaledWidth = rect.width * zoomLevel;
    const scaledHeight = rect.height * zoomLevel;

    // After scaling from (0, 0), where does our cursor point end up?
    const scaledCursorX = magnifierPos.x * zoomLevel;
    const scaledCursorY = magnifierPos.y * zoomLevel;

    // We want the scaled cursor point to be at the center of the magnifier
    // So translate the wrapper
    let wrapperLeft = halfSize - scaledCursorX;
    let wrapperTop = halfSize - scaledCursorY;

    // Clamp the wrapper position so we don't show empty space
    // The wrapper can't be positioned more than 0 (showing left/top edge)
    // And can't be positioned less than -(scaledWidth - magnifierSize) (showing right/bottom edge)
    wrapperLeft = Math.min(0, Math.max(wrapperLeft, magnifierSize - scaledWidth));
    wrapperTop = Math.min(0, Math.max(wrapperTop, magnifierSize - scaledHeight));

    return {
      container: {
        width: `${magnifierSize}px`,
        height: `${magnifierSize}px`,
        left: `${boxLeft}px`,
        top: `${boxTop}px`,
      },
      wrapper: {
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        transform: `scale(${zoomLevel})`,
        transformOrigin: '0 0',
        left: `${wrapperLeft}px`,
        top: `${wrapperTop}px`,
      }
    };
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <div>
      {/* Main Image Container */}
      <div
        ref={imageRef}
        className="relative aspect-square bg-black/10 rounded-lg overflow-hidden cursor-crosshair"
        onMouseEnter={() => setShowMagnifier(true)}
        onMouseLeave={() => setShowMagnifier(false)}
        onMouseMove={handleMouseMove}
        onTouchStart={() => setShowMagnifier(true)}
        onTouchEnd={() => setShowMagnifier(false)}
        onTouchMove={handleTouchMove}
      >
        {/* Loading Spinner */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/10 z-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white/30"></div>
          </div>
        )}

        {/* Base Image */}
        <img
          src={images[selectedIndex].url}
          alt={alt}
          className="absolute inset-0 w-full h-full object-cover"
          onLoad={() => setIsLoading(false)}
        />

        {/* Magnifier */}
        {showMagnifier && (() => {
          const magnifierStyles = getMagnifierStyle();
          return (
            <div
              className="absolute pointer-events-none border-2 border-purple-500 rounded-lg shadow-2xl z-20 overflow-hidden"
              style={magnifierStyles.container}
            >
              <div
                className="absolute"
                style={magnifierStyles.wrapper}
              >
                <img
                  src={getHighResUrl(images[selectedIndex].url)}
                  alt="Magnified view"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          );
        })()}

        {/* Overlay Image (fades in during transition) */}
        {showOverlay && (
          <img
            src={images[nextIndex].url}
            alt={alt}
            className="image-overlay absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out opacity-0"
            style={{ opacity: 0 }}
          />
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="py-4">
          <div className="flex gap-2 overflow-x-auto">
            {images.map((image, index) => (
              <button
                key={index}
                onClick={() => handleImageChange(index)}
                className={`flex-shrink-0 w-20 h-20 rounded border-2 overflow-hidden transition-all duration-200 cursor-pointer ${
                  index === selectedIndex
                    ? "opacity-100 shadow-lg"
                    : "opacity-30 hover:opacity-60"
                }`}
                style={{
                  borderColor: index === selectedIndex ? '#936DFF' : 'rgba(147, 109, 255, 1)',
                  boxShadow: index === selectedIndex ? '0 10px 15px -3px rgba(147, 109, 255, 0.3), 0 4px 6px -2px rgba(147, 109, 255, 0.05)' : undefined
                }}
              >
                <img
                  src={image.url}
                  alt={`${alt} thumbnail ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}